import React, { useState, useEffect, useRef } from 'react';
import { parseISO, format, subDays, eachDayOfInterval } from 'date-fns';
import { getEndpointHistory } from '../../../shared/api/publicApi';
import '../../../styles/features/status/UptimeHeatmap.css';

function cellColor(uptime, compact) {
  if (uptime === null || uptime === undefined) return compact ? 'var(--heatmap-empty-compact, rgba(255,255,255,0.04))' : 'var(--heatmap-empty, rgba(255,255,255,0.06))';
  if (uptime >= 99)  return '#10b981';
  if (uptime >= 95)  return '#f59e0b';
  return '#ef4444';
}

function UptimeHeatmap({ endpointId, days = 90, compact = false, history = null }) {
  const [data, setData]       = useState(null);  // map: dateStr → { uptime, checks }
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(null);  // { text, x, y }
  const containerRef          = useRef(null);

  useEffect(() => {
    // Preloaded data path: build the lookup map from the prop, no request.
    if (history != null) {
      const map = {};
      for (const row of history) {
        map[row.date] = { uptime: row.uptime, checks: row.checks };
      }
      setData(map);
      setLoading(false);
      return;
    }

    if (!endpointId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);

    getEndpointHistory(endpointId, days)
      .then(res => {
        if (cancelled) return;
        const map = {};
        for (const row of (res.data.data || [])) {
          map[row.date] = { uptime: row.uptime, checks: row.checks };
        }
        setData(map);
      })
      .catch(() => setData({}))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [endpointId, days, history]);

  if (loading) return compact ? null : <div className="heatmap-loading">Loading history…</div>;

  // Build the full list of days (oldest → newest, padded to full weeks)
  const today    = new Date();
  const oldest   = subDays(today, days - 1);
  const allDays  = eachDayOfInterval({ start: oldest, end: today });

  // Pad the front so the first day falls on Monday (isoWeekday 1)
  const startPad = (allDays[0].getDay() + 6) % 7; // Mon=0
  const cells    = [
    ...Array(startPad).fill(null),
    ...allDays.map(d => format(d, 'yyyy-MM-dd')),
  ];

  // Split into weeks (columns)
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  // Month labels for full view
  const monthLabels = [];
  if (!compact) {
    weeks.forEach((week, wi) => {
      const firstReal = week.find(Boolean);
      if (!firstReal) return;
      const d = parseISO(firstReal);
      const label = format(d, 'MMM');
      if (wi === 0 || (d.getDate() <= 7 && monthLabels[monthLabels.length - 1]?.label !== label)) {
        monthLabels.push({ wi, label });
      }
    });
  }

  const cellSize = compact ? 6 : 12;
  const gap      = compact ? 2 : 3;

  return (
    <div className={`uptime-heatmap ${compact ? 'uptime-heatmap--compact' : ''}`} ref={containerRef}>
      {!compact && (
        <div className="heatmap-header">
          <span className="heatmap-title">90-Day History</span>
          <div className="heatmap-legend">
            <span>Less</span>
            {[null, 100, 97, 90].map((v, i) => (
              <div key={i} className="heatmap-legend-cell" style={{ background: cellColor(v, false) }} />
            ))}
            <span>More</span>
          </div>
        </div>
      )}

      {!compact && (
        <div className="heatmap-months" style={{ paddingLeft: `${cellSize + gap}px` }}>
          {monthLabels.map(({ wi, label }) => (
            <span
              key={wi}
              className="heatmap-month-label"
              style={{ left: `${wi * (cellSize + gap)}px` }}
            >
              {label}
            </span>
          ))}
        </div>
      )}

      <div className="heatmap-grid" style={{ gap: `${gap}px` }}>
        {/* Day-of-week labels */}
        {!compact && (
          <div className="heatmap-dow" style={{ gap: `${gap}px`, width: `${cellSize}px` }}>
            {['M','','W','','F','',''].map((l, i) => (
              <div key={i} className="heatmap-dow-label" style={{ height: `${cellSize}px` }}>{l}</div>
            ))}
          </div>
        )}

        {weeks.map((week, wi) => (
          <div key={wi} className="heatmap-col" style={{ gap: `${gap}px` }}>
            {week.map((dateStr, di) => {
              if (!dateStr) {
                return <div key={di} className="heatmap-cell heatmap-cell--empty" style={{ width: cellSize, height: cellSize }} />;
              }
              const entry  = data?.[dateStr];
              const uptime = entry?.uptime ?? null;
              const checks = entry?.checks ?? 0;
              const color  = cellColor(uptime, compact);
              return (
                <div
                  key={di}
                  className="heatmap-cell"
                  style={{ width: cellSize, height: cellSize, background: color }}
                  onMouseEnter={e => {
                    if (compact) return;
                    const rect = containerRef.current?.getBoundingClientRect();
                    const cr   = e.currentTarget.getBoundingClientRect();
                    setTooltip({
                      text:    uptime !== null ? `${dateStr}: ${uptime}% uptime (${checks} checks)` : `${dateStr}: no data`,
                      x: cr.left - (rect?.left ?? 0) + cellSize / 2,
                      y: cr.top  - (rect?.top  ?? 0) - 8,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                />
              );
            })}
          </div>
        ))}
      </div>

      {tooltip && (
        <div
          className="heatmap-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}

export default UptimeHeatmap;
